import type { CostLibraryTask } from "@shared/schema";
import { getCostRecipe } from "@workspace/api-client-react";
import { costRecipeSchema, deriveCostRecipe, type CostRecipe } from "@workspace/api-zod/cost-recipe";
import { ensureLengthDimensions, priceRecipeFromDimensions as priceRecipeForQuote } from "@workspace/api-zod/dimension-pricing";
import type { QuantityDerivation } from "@workspace/api-zod/quantity-dimensions";
import { quoteRecipeSplit, recipeAtQuotePrice } from "@/components/quote-item-recipe-breakdown";
import { rescaleBreakdown } from "./cost-breakdown";
import { getCostTaskNavLocation } from "./cost-library-labels";

export function catalogueTaskKey(task: Pick<CostLibraryTask, "id" | "source">) {
  return `${task.source === "custom" ? "custom" : "default"}:${task.id}`;
}

export interface CatalogueQuoteItem {
  id: string;
  catalogueTaskKey: string;
  costLibraryTaskId: string;
  costLibraryPrice: number;
  category: string;
  trade: string;
  subcategory?: string;
  name: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  labourHours: number;
  labourCost: number;
  materialCost: number;
  plantCost: number;
  inclusions: string[];
  exclusions: string[];
  quoteCostRecipe?: CostRecipe;
  quantityDerivation?: QuantityDerivation;
}

/** Explicit catalogue picks never go through AI or fuzzy matching. Fetch the
 * effective recipe before insertion so collapsed rows also save their materials. */
export async function createCatalogueQuoteItem(task: CostLibraryTask, options?: {
  band?: "low" | "standard" | "high";
  quantity?: number;
  dimensions?: QuantityDerivation;
  sectionText?: string;
}): Promise<CatalogueQuoteItem> {
  if (task.isHidden || task.isActive === false) throw new Error("This work item is no longer available.");
  const baseRate = Number(task.rateTypical);
  let unitCost = Number(options?.band === "low" ? task.rateLow : options?.band === "high" ? task.rateHigh : task.rateTypical);
  const quantity = options?.quantity ?? 1;
  if (!Number.isFinite(quantity) || quantity < 0) throw new Error("Enter a valid quantity.");
  if (!Number.isFinite(unitCost) || unitCost < 0) throw new Error("This item does not have a valid library rate.");
  let quoteCostRecipe: CostRecipe | undefined;
  const context = options?.sectionText ?? `${task.name} ${task.description ?? ""}`;
  let dimensions = ensureLengthDimensions(options?.dimensions, task.unit, quantity, context, undefined, { itemName: task.name });
  // Only the effective recipe is usable. A legacy personal rate may retain a
  // default recipe underneath it without having a compatible recipe of its own.
  if (task.hasRecipe) {
    const state = await getCostRecipe(task.source === "custom" ? "custom" : "default", task.id);
    if (!state.recipe) throw new Error("The breakdown has changed. Refresh the library and try again.");
    if (state.unit.trim().toLowerCase() !== task.unit.trim().toLowerCase()
      || Number(state.rateTypical) !== baseRate) {
      throw new Error("The library rate or unit has changed. Refresh the library and try again.");
    }
    const reference = costRecipeSchema.parse(state.recipe);
    dimensions = ensureLengthDimensions(dimensions, task.unit, quantity, context, reference, { itemName: task.name });
    quoteCostRecipe = priceRecipeForQuote(reference, unitCost, dimensions, context, quantity, task.unit);
    unitCost = Number(deriveCostRecipe(quoteCostRecipe).rateTypical);
  }
  const split = quoteCostRecipe ? quoteRecipeSplit(quoteCostRecipe, quantity) : (() => {
    const s = rescaleBreakdown({
      labour: Number(task.labourPct) || 0, material: Number(task.materialPct) || 0,
      plant: Number(task.plantPct) || 0,
    }, unitCost * quantity, task.category);
    return { labourCost: s.labour, materialCost: s.material, plantCost: s.plant };
  })();
  return {
    id: crypto.randomUUID(), catalogueTaskKey: catalogueTaskKey(task),
    costLibraryTaskId: task.id, costLibraryPrice: unitCost,
    category: task.category, trade: task.trade,
    subcategory: getCostTaskNavLocation(task)?.item.id,
    name: task.name, description: task.description ?? "", quantity,
    unit: task.unit, unitCost, labourHours: 0, ...split,
    inclusions: [], exclusions: [],
    ...(dimensions ? { quantityDerivation: dimensions } : {}),
    ...(quoteCostRecipe ? { quoteCostRecipe } : {}),
  };
}

/** A manual edit reconciles the quote snapshot, never the library reference. */
export function resizeCatalogueRecipe<T extends {
  quantity: number; unitCost: number; quoteCostRecipe?: CostRecipe | null;
}>(item: T): T & { labourCost?: number; materialCost?: number; plantCost?: number } {
  if (!item.quoteCostRecipe) return item;
  const unitCost = Math.round(item.unitCost * 100) / 100;
  const recipe = recipeAtQuotePrice(item.quoteCostRecipe, unitCost);
  return { ...item, unitCost, quoteCostRecipe: recipe, ...quoteRecipeSplit(recipe, item.quantity) };
}
